import React, { useMemo, useState } from 'react';
import Avatar from './Avatar';
import Icon from './Icon';
import { AVATARS, isUnlocked, unlockLevel } from './avatarRegistry';
import { useStudent } from '../student/StudentContext';
import './AvatarSelector.css';

/**
 * Curated picker over the avatar registry. Purely controlled: the parent owns the selected id and the
 * persistence (PATCH /api/portal/avatar). A radiogroup, so it is keyboard- and screen-reader-operable
 * (each option is a >=44px tap target).
 *
 * Avatars unlock with the student's real Level (registry `unlock`, enforced server-side). Open ones come first,
 * then by level, so what you can pick now is always at the top and the next goal is obvious. A locked option shows
 * its lock and the Level it needs, and tapping it explains — it never silently does nothing. The avatar a student
 * already has is always selectable, even if the table changes later.
 */
export default function AvatarSelector({ selectedId, onSelect, name, currentId }) {
  const { player } = useStudent();
  const level = player?.level || 1;
  const [hint, setHint] = useState('');

  const ordered = useMemo(
    () => [...AVATARS].sort((a, b) => unlockLevel(a) - unlockLevel(b)),
    []
  );
  const owned = (a) => isUnlocked(a, level) || a.id === currentId;
  const unlockedCount = ordered.filter(owned).length;

  const choose = (a) => {
    if (owned(a)) { setHint(''); onSelect(a.id); return; }
    setHint(`${a.name} unlocks at Level ${unlockLevel(a)} — you're Level ${level}. Keep practising!`);
  };

  return (
    <>
      <p className="z-avsel-count" aria-live="polite">{unlockedCount} of {AVATARS.length} unlocked</p>
      <div className="z-avsel" role="radiogroup" aria-label="Choose your Zaptude avatar">
        {ordered.map(a => {
          const active = a.id === selectedId;
          const locked = !owned(a);
          return (
            <button
              key={a.id}
              type="button"
              role="radio"
              aria-checked={active}
              aria-disabled={locked || undefined}
              aria-label={locked ? `${a.name}, locked — unlocks at Level ${unlockLevel(a)}` : a.name}
              className={`z-avsel-option${active ? ' z-avsel-option--active' : ''}${locked ? ' z-avsel-option--locked' : ''}`}
              onClick={() => choose(a)}
            >
              <span className="z-avsel-art">
                <Avatar avatarId={a.id} name={name} size={64} />
                {active && <span className="z-avsel-check"><Icon name="check" size={14} strokeWidth={3} /></span>}
                {locked && <span className="z-avsel-lock"><Icon name="lock" size={16} /></span>}
              </span>
              <span className="z-avsel-name">{a.name}</span>
              {locked && <span className="z-avsel-level">Level {unlockLevel(a)}</span>}
            </button>
          );
        })}
      </div>
      {hint && <p className="z-avsel-hint" role="status">{hint}</p>}
    </>
  );
}
